// pages/AdminPanel.js
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/AdminPanel.css";

const API_BASE_URL = "https://jobs.mpdatahub.com/api";

const CATEGORIES = [
  "Recruitment",
  "Culture",
  "Compliance",
  "Performance",
  "Well-being",
  "DEI",
];

export default function AdminPanel() {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [blogToDelete, setBlogToDelete] = useState(null);
  const [toast, setToast] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    featured: 0,
    byCategory: {},
  });

  useEffect(() => {
    fetchBlogs();
  }, []);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/blogs`);
      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();

      // Transform the data to match the expected format
      const transformedData = data.map((blog) => {
        // Parse tags if it's a string, otherwise use array or empty array
        let parsedTags = blog.tags || [];
        if (typeof parsedTags === "string") {
          try {
            parsedTags = JSON.parse(parsedTags);
          } catch (e) {
            parsedTags = parsedTags ? [parsedTags] : [];
          }
        }
        if (!Array.isArray(parsedTags)) {
          parsedTags = [];
        }

        return {
          id: blog.id,
          title: blog.title,
          excerpt: blog.excerpt,
          content: blog.content,
          category: blog.category,
          author: blog.author || "Anonymous",
          authorRole: blog.authorRole || "Contributor",
          tags: parsedTags,
          featured:
            blog.featured === true ||
            blog.featured === 1 ||
            blog.featured === "1",
          readTime: blog.readTime || "5 min read",
          date: blog.date,
          image: blog.image_url, // Map image_url to image
          slug: blog.slug,
        };
      });

      setBlogs(transformedData);
      calculateStats(transformedData);
      setError(null);
    } catch (err) {
      console.error("Error fetching blogs:", err);
      setError("Failed to load blogs. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (blogsData) => {
    const byCategory = {};
    blogsData.forEach((blog) => {
      byCategory[blog.category] = (byCategory[blog.category] || 0) + 1;
    });

    setStats({
      total: blogsData.length,
      featured: blogsData.filter((b) => b.featured).length,
      byCategory,
    });
  };

  const showToastMessage = (msg, type = "success") => {
    setToast({ msg, type });
  };

  const handleDeleteClick = (blog) => {
    setBlogToDelete(blog);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!blogToDelete) return;

    try {
      const response = await fetch(
        `${API_BASE_URL}/blogs-destroy/${blogToDelete.id}`,
        {
          method: "GET",
        },
      );
      if (!response.ok) throw new Error("Failed to delete");

      await fetchBlogs();
      showToastMessage(`"${blogToDelete.title}" deleted successfully`);
      setShowDeleteModal(false);
      setBlogToDelete(null);
    } catch (err) {
      console.error("Error deleting blog:", err);
      showToastMessage("Failed to delete blog", "error");
    }
  };

  const handleToggleFeatured = async (blog) => {
    try {
      const response = await fetch(`${API_BASE_URL}/blogs-update/${blog.id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ featured: !blog.featured ? 1 : 0 }),
      });
      if (!response.ok) throw new Error("Failed to update");

      await fetchBlogs();
      showToastMessage(
        `Blog ${!blog.featured ? "featured" : "unfeatured"} successfully`,
      );
    } catch (err) {
      console.error("Error toggling featured:", err);
      showToastMessage("Failed to update blog", "error");
    }
  };

  const filteredBlogs = blogs.filter((blog) => {
    const matchCategory =
      selectedCategory === "All" || blog.category === selectedCategory;
    const matchSearch =
      !searchQuery ||
      blog.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      blog.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
      blog.author?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      blog.tags?.some((tag) =>
        tag.toLowerCase().includes(searchQuery.toLowerCase()),
      );
    return matchCategory && matchSearch;
  });

  if (loading) {
    return (
      <div className="admin-panel">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      {/* Header */}
      <header className="admin-header">
        <div className="header-content">
          <div className="logo-section">
            <div className="logo-icon">📊</div>
            <div>
              <h1 className="admin-title">Content Management</h1>
              <p className="admin-subtitle">Manage your blog posts</p>
            </div>
          </div>
          <div className="header-actions">
            <button
              className="btn-primary"
              onClick={() => navigate("/blog/new")}
            >
              + Create New Post
            </button>
            <button className="btn-secondary" onClick={() => navigate("/blog")}>
              View Blog
            </button>
          </div>
        </div>
      </header>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">📝</div>
          <div className="stat-info">
            <h3>Total Posts</h3>
            <p className="stat-number">{stats.total}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">⭐</div>
          <div className="stat-info">
            <h3>Featured Posts</h3>
            <p className="stat-number">{stats.featured}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📂</div>
          <div className="stat-info">
            <h3>Categories</h3>
            <p className="stat-number">
              {Object.keys(stats.byCategory).length}
            </p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">✏️</div>
          <div className="stat-info">
            <h3>Last Updated</h3>
            <p className="stat-number">{blogs[0]?.date || "N/A"}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="search-box">
          <input
            type="text"
            className="search-input"
            placeholder="🔍 Search posts by title, author, or tags..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="category-filters">
          <button
            className={`filter-chip ${selectedCategory === "All" ? "active" : ""}`}
            onClick={() => setSelectedCategory("All")}
          >
            All ({stats.total})
          </button>
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              className={`filter-chip ${selectedCategory === cat ? "active" : ""}`}
              onClick={() => setSelectedCategory(cat)}
            >
              {cat} ({stats.byCategory[cat] || 0})
            </button>
          ))}
        </div>
      </div>

      {/* Blog List */}
      <div className="blogs-table-container">
        {error ? (
          <div className="error-state">
            <p>⚠️ {error}</p>
            <button onClick={fetchBlogs} className="btn-primary">
              Retry
            </button>
          </div>
        ) : filteredBlogs.length === 0 ? (
          <div className="empty-state">
            <p>📭 No posts found</p>
            <button
              onClick={() => navigate("/blog/new")}
              className="btn-primary"
            >
              Create your first post
            </button>
          </div>
        ) : (
          <table className="blogs-table">
            <thead>
              <tr>
                <th>Post</th>
                <th>Category</th>
                <th>Author</th>
                <th>Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredBlogs.map((blog) => (
                <tr key={blog.id}>
                  <td className="post-info">
                    <img
                      src={blog.image}
                      alt={blog.title}
                      className="post-thumbnail"
                      onError={(e) => {
                        e.target.src =
                          "https://via.placeholder.com/80x60?text=No+Image";
                      }}
                    />
                    <div>
                      <div className="post-title">{blog.title}</div>
                      <div className="post-excerpt">
                        {blog.excerpt?.substring(0, 100) || ""}...
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="category-badge">{blog.category}</span>
                  </td>
                  <td>{blog.author || "Anonymous"}</td>
                  <td>{blog.date}</td>
                  <td>
                    <button
                      className={`featured-toggle ${blog.featured ? "active" : ""}`}
                      onClick={() => handleToggleFeatured(blog)}
                    >
                      {blog.featured ? "⭐ Featured" : "☆ Set Featured"}
                    </button>
                  </td>
                  <td className="action-buttons">
                    <button
                      className="action-btn edit"
                      onClick={() => navigate(`/blog/edit/${blog.id}`)}
                      title="Edit"
                    >
                      ✏️
                    </button>
                    <button
                      className="action-btn delete"
                      onClick={() => handleDeleteClick(blog)}
                      title="Delete"
                    >
                      🗑️
                    </button>
                    <button
                      className="action-btn view"
                      onClick={() => window.open(`/blog/${blog.id}`, "_blank")}
                      title="View"
                    >
                      👁️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && blogToDelete && (
        <div
          className="modal-overlay"
          onClick={() => setShowDeleteModal(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirm Delete</h2>
              <button
                className="modal-close"
                onClick={() => setShowDeleteModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>
                Are you sure you want to delete "
                <strong>{blogToDelete.title}</strong>"?
              </p>
              <p className="warning-text">This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button className="btn-danger" onClick={confirmDelete}>
                Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <div className={`toast-notification ${toast.type}`}>{toast.msg}</div>
      )}
    </div>
  );
}
